import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui";
import { DenkerLogo } from "@/components/ui/logo";
import { useT } from "@/i18n";
import { canvasFeedbackMessages } from "@/i18n/messages/canvas-feedback";
import { cn } from "@/lib/cn";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";
import { OPEN_FEEDBACK_EVENT } from "./feedback-events";
import { FeedbackForm } from "@/components/settings/feedback-form";

export function FeedbackButton() {
  const t = useT(canvasFeedbackMessages);
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const pendingRef = useRef(false);
  const handleClose = useCallback(() => {
    if (!pendingRef.current) setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(event: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        !buttonRef.current?.contains(event.target as Node)
      ) {
        handleClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, handleClose]);

  useEffect(() => {
    const openFeedback = () => setOpen(true);
    window.addEventListener(OPEN_FEEDBACK_EVENT, openFeedback);
    return () => window.removeEventListener(OPEN_FEEDBACK_EVENT, openFeedback);
  }, []);

  return (
    <div className="relative">
      <Button
        ref={buttonRef}
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => (open ? handleClose() : setOpen(true))}
        className={cn(
          surfaceRoleClassName("rail"),
          "h-8 w-8 border border-glass-stroke",
          open && "bg-[var(--color-surface-selected)]",
        )}
        data-testid="feedback-button"
        aria-label={t("sendFeedback")}
        aria-expanded={open}
        {...surfaceRoleAttributes("rail", {
          nativeLevel: "island",
          nativeGroup: "feedback",
        })}
      >
        <DenkerLogo variant="symbol" color="auto" height={14} />
      </Button>
      {open && (
        <div
          ref={popoverRef}
          className={cn(
            surfaceRoleClassName("menu"),
            "absolute left-1/2 top-full z-50 w-72 -translate-x-1/2",
            "mt-2 border border-glass-stroke",
            "animate-in fade-in slide-in-from-top-2 duration-150",
          )}
          data-testid="feedback-popover"
          {...surfaceRoleAttributes("menu", {
            nativeLevel: "root",
            nativeGroup: "menus",
          })}
        >
          <FeedbackForm
            onClose={handleClose}
            onPendingChange={(value) => {
              pendingRef.current = value;
            }}
          />
        </div>
      )}
    </div>
  );
}
